import React from "react";
import ModalShell from "./ModalShell";

/**
 * Modal — Warm Precision design-system primitive that wraps <ModalShell> with
 * the standard backdrop, portal, Esc-to-close, backdrop-click-to-close, focus
 * trap, and body-scroll lock. Pages should stop hand-rolling
 * `fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4`
 * wrappers — that pattern used to be duplicated across ~60 files and each
 * copy had to be individually kept in sync (backdrop opacity, z-index,
 * Esc handling, focus restore) whenever the design changed.
 *
 * Composes ModalShell's existing Header/Body/Footer. ModalShell itself now
 * owns the backdrop/centering/portal/focus-trap when given `dialogOpen` —
 * it wraps everything in a real @headlessui/react <Dialog> (WCAG 2.1.2 /
 * 2.4.3), which auto-portals to its OWN portal root at document.body. That's
 * why Modal no longer does its own `createPortal`/backdrop div: <Dialog>'s
 * portal would otherwise teleport ModalShell's card out from under a
 * separate outer centering wrapper, leaving it unpositioned. See ModalShell's
 * `dialogOpen`/`onDialogClose`/`onBackdropClick`/`backdropClassName` props.
 * `closeOnEsc={false}` is preserved by gating `onDialogClose` to a no-op —
 * Dialog still traps focus, it just never calls back on Escape.
 *
 * Usage:
 *   <Modal open={open} onClose={close} size="md">
 *     <ModalShell.Header title="Edit campaign" onClose={close} />
 *     <ModalShell.Body>…</ModalShell.Body>
 *     <ModalShell.Footer>
 *       <SecondaryButton onClick={close}>Cancel</SecondaryButton>
 *       <PrimaryButton onClick={save}>Save</PrimaryButton>
 *     </ModalShell.Footer>
 *   </Modal>
 *
 * For a critical confirmation where an accidental backdrop click or Esc
 * should NOT dismiss (e.g. mid-destructive-action), pass
 * closeOnBackdropClick={false} closeOnEsc={false}.
 *
 * Need a side-docked sliding panel instead of a centered card? Use <Drawer>
 * — same overlay contract, different visual shell.
 */

type Size = "sm" | "md" | "lg" | "xl" | "2xl" | "5xl";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  size?: Size;
  height?: "auto" | "tall" | "full";
  closeOnEsc?: boolean;
  closeOnBackdropClick?: boolean;
  className?: string;
  shellClassName?: string;
  /** Forwarded to ModalShell's root — use when there's no visible <ModalShell.Header title> to label the dialog. */
  "aria-label"?: string;
  "aria-labelledby"?: string;
  children?: React.ReactNode;
}

function Modal({
  open,
  onClose,
  size = "md",
  height = "auto",
  closeOnEsc = true,
  closeOnBackdropClick = true,
  className = "",
  shellClassName = "",
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  children
}: ModalProps) {
  if (!open) return null;

  return (
    <ModalShell
      size={size}
      height={height}
      className={shellClassName}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      dialogOpen={open}
      onDialogClose={closeOnEsc ? onClose : () => {}}
      onBackdropClick={closeOnBackdropClick ? onClose : undefined}
      backdropClassName={className}
    >
      {children}
    </ModalShell>
  );
}

export default Modal;
